/**
 * Persisted mock backend state.
 *
 * Lives in `localStorage` under a single versioned key. Schema
 * migrations are explicit — bump `SCHEMA_VERSION` and add a handler if
 * the shape changes.
 *
 * The store holds fully-formed `GiRecord`s (manifest + signed_entity +
 * contract + lineage + attestations), plus an activity log. The Vault
 * view is a pure projection: `listVaultSnapshot()` derives
 * `VaultSnapshot` from these records.
 */

import type {
  ActivityEvent,
  AttestationRecord,
  BehavioralContract,
  EntityId,
  GiStatus,
  GiSummary,
  Lineage,
  Manifest,
  SignedEntity,
  TrustedTimestamp,
  VaultSnapshot,
  VaultStats,
} from '../types';

const STORAGE_KEY = 'gi-engine.mock-store.v1';
const SCHEMA_VERSION = 1;

export interface GiRecord {
  manifest: Manifest;
  signed_entity: SignedEntity;
  contract: BehavioralContract;
  lineage: Lineage;
  attestations: AttestationRecord[];
  // Statuses are derived lazily, but cached for stable ordering.
  statuses: GiStatus[];
  // Display counters — updated by mock event handlers.
  counters: {
    signatures: number;
    verifications: number;
    executions: number;
    forks: number;
  };
  // Secret keys stay inside the mock; they never cross the `api/`
  // boundary into the React tree. Hex-encoded so JSON.stringify works.
  secret_key_hex: string;
}

interface PersistedState {
  version: number;
  records: GiRecord[];
  activity: ActivityEvent[];
  /** Running counters mirrored in the Vault header. Derived on read
   *  in case a future event skips the persist step. */
  stats_cache: VaultStats;
  /** Set once the user has triggered the one-click demo seeder, so
   *  the Vault empty state can distinguish "never seeded" from
   *  "seeded then cleared". */
  seeded_demo: boolean;
}

function emptyState(): PersistedState {
  return {
    version: SCHEMA_VERSION,
    records: [],
    activity: [],
    stats_cache: {
      total_gis: 0,
      total_signatures: 0,
      total_verifications: 0,
      total_forks: 0,
    },
    seeded_demo: false,
  };
}

function load(): PersistedState {
  if (typeof localStorage === 'undefined') return emptyState();
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return emptyState();
  try {
    const parsed = JSON.parse(raw) as PersistedState;
    if (parsed.version !== SCHEMA_VERSION) return emptyState();
    return parsed;
  } catch {
    return emptyState();
  }
}

function save(state: PersistedState): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ─── Public surface ─────────────────────────────────────────────────

export function reset(): void {
  save(emptyState());
}

export function hasSeededDemo(): boolean {
  return load().seeded_demo;
}

export function markSeededDemo(): void {
  const s = load();
  s.seeded_demo = true;
  save(s);
}

export function listRecords(): GiRecord[] {
  return load().records;
}

export function getRecord(root_id: EntityId): GiRecord | undefined {
  return load().records.find((r) => r.manifest.root_id === root_id);
}

export function upsertRecord(rec: GiRecord): void {
  const s = load();
  const idx = s.records.findIndex((r) => r.manifest.root_id === rec.manifest.root_id);
  if (idx >= 0) s.records[idx] = rec;
  else s.records.push(rec);
  s.stats_cache = recomputeStats(s.records);
  save(s);
}

export function appendActivity(ev: ActivityEvent): void {
  const s = load();
  s.activity.unshift(ev); // newest first
  if (s.activity.length > 200) s.activity = s.activity.slice(0, 200);
  save(s);
}

export function snapshot(): VaultSnapshot {
  const s = load();
  const gis: GiSummary[] = s.records
    .map(recordToSummary)
    .sort((a, b) => b.created_at.iso.localeCompare(a.created_at.iso));
  return {
    stats: s.stats_cache,
    gis,
    activity: s.activity.slice(0, 25),
  };
}

function recordToSummary(r: GiRecord): GiSummary {
  return {
    root_id: r.manifest.root_id,
    version: r.manifest.version,
    name: r.manifest.name,
    description: r.manifest.description,
    domain: r.manifest.domain,
    author_name: r.manifest.author.name,
    created_at: r.manifest.created_at,
    status: r.statuses,
    stats: r.counters,
  };
}

function recomputeStats(records: GiRecord[]): VaultStats {
  return records.reduce(
    (acc, r) => ({
      total_gis: acc.total_gis + 1,
      total_signatures: acc.total_signatures + r.counters.signatures,
      total_verifications: acc.total_verifications + r.counters.verifications,
      total_forks: acc.total_forks + r.counters.forks,
    }),
    { total_gis: 0, total_signatures: 0, total_verifications: 0, total_forks: 0 },
  );
}

export function nowTimestamp(): TrustedTimestamp {
  return { iso: new Date().toISOString(), source: 'Monotonic' };
}
